// Shared bias utility functions - can be used by both server and client components
import { RELIABILITY } from '../constants/bias.js'

// Tailwind classes per coverage color key (mirrors the pipeline's COVERAGE_COLORS).
// 3-color legend: blue = left-leaning, red = right-leaning, gray = neutral.
// polarized / left_center / center_right are the pre-2026-10-22 labels, kept
// so an old row still renders.
export const COVERAGE_CLASSES = {
  polarized: 'text-muted-foreground',
  left_center: 'text-muted-foreground',
  center_right: 'text-muted-foreground',
  left: 'text-blue-600 dark:text-blue-400',
  leans_left: 'text-blue-600 dark:text-blue-400',
  right: 'text-red-600 dark:text-red-400',
  leans_right: 'text-red-600 dark:text-red-400',
  center: 'text-muted-foreground',
  balanced: 'text-muted-foreground',
  none: 'text-muted-foreground',
}

// Display remap. "Mostly Left/Right" is shown as stored (2026-09-26: the
// site used to say "Leans Left/Right", borrowing the per-outlet scale's
// words; a story label now says "Mostly", like the newsletter and the
// pipeline, and "Leans" is left to describe outlets).
const COVERAGE_DISPLAY_TEXT = {
  // Both wings present reads as balance to a reader — the center-thin
  // nuance lives in the spectrum strip, not the word.
  Polarized: 'Balanced',
}

export function coverageDisplayText(text) {
  return COVERAGE_DISPLAY_TEXT[text] ?? text
}

// Canonical coverage label from left/center/right counts.
// MUST mirror pano-pipeline bias_utils.coverage_label_from_counts. Keep in sync.
// The lean is left against right (Sam, 2026-10-22); center shows in the bar:
//   - center >= 60% of all            -> Mostly Center
//   - left and right within 3 articles -> Balanced (chance, not a lean)
//   - right's share of left + right:  >= 75 Mostly Right, >= 60 Leans Right,
//     <= 25 Mostly Left, <= 40 Leans Left, else Balanced
// It replaced "Left & Center" / "Center & Right" / "Polarized", which called
// a story with half its coverage from the right Balanced.
export const MIN_LEAN_GAP = 4
export function coverageFromCounts(left, center, right) {
  const total = left + center + right
  if (!total) return { text: 'No sources', key: 'none' }
  if ((center / total) * 100 >= 60)
    return { text: 'Mostly Center', key: 'center' }
  if (Math.abs(left - right) < MIN_LEAN_GAP)
    return { text: 'Balanced', key: 'balanced' }
  const rs = (right / (left + right)) * 100
  if (rs >= 75) return { text: 'Mostly Right', key: 'right' }
  if (rs >= 60) return { text: 'Leans Right', key: 'leans_right' }
  if (rs <= 25) return { text: 'Mostly Left', key: 'left' }
  if (rs <= 40) return { text: 'Leans Left', key: 'leans_left' }
  return { text: 'Balanced', key: 'balanced' }
}

// Compute a coverage label from a set of articles (fallback path).
export function getSubtopicBiasLabel(articles) {
  let left = 0,
    center = 0,
    right = 0
  articles.forEach((article) => {
    const bias = article.outlet?.bias ?? 0
    if (bias < -6) left++
    else if (bias > 6) right++
    else center++
  })
  const { text, key } = coverageFromCounts(left, center, right)
  return { text, color: COVERAGE_CLASSES[key] }
}

// Prefer the pipeline-stored coverage label (single source of truth);
// fall back to computing from articles for rows not yet backfilled.
export function coverageBiasLabel(node) {
  if (node?.coverage_label) {
    return {
      text: coverageDisplayText(node.coverage_label),
      color: COVERAGE_CLASSES[node.coverage_color] ?? COVERAGE_CLASSES.none,
    }
  }
  return getSubtopicBiasLabel(node?.articles ?? [])
}

// Class for a coverage label that arrives as display text (e.g. newsletter
// JSON stores "Mostly Right" with a hex color; we re-map to theme classes).
const COVERAGE_TEXT_KEYS = {
  'Mostly Left': 'left',
  'Mostly Right': 'right',
  'Leans Left': 'leans_left',
  'Leans Right': 'leans_right',
  'Mostly Center': 'center',
  Polarized: 'polarized',
  'Left & Center': 'left_center',
  'Center & Right': 'center_right',
  Balanced: 'balanced',
}

export function coverageClassFromText(text) {
  return COVERAGE_CLASSES[COVERAGE_TEXT_KEYS[text] ?? 'none']
}

// The colour key for a label that arrives as display text, so a newsletter
// row can wear the same LeanBar as a live story. Null when unknown.
export function coverageKeyFromText(text) {
  return COVERAGE_TEXT_KEYS[text] ?? null
}

export function getBiasLabel(bias) {
  if (bias <= -18)
    return {
      label: 'Far Left',
      color: 'text-blue-800 dark:text-blue-300',
      bg: 'bg-blue-200 dark:bg-blue-900/50',
    }
  if (bias <= -12)
    return {
      label: 'Left',
      color: 'text-blue-600 dark:text-blue-400',
      bg: 'bg-blue-100 dark:bg-blue-900/30',
    }
  if (bias < -6)
    return {
      label: 'Leans Left',
      color: 'text-blue-500 dark:text-blue-400',
      bg: 'bg-blue-50 dark:bg-blue-900/20',
    }
  if (bias <= 6)
    return { label: 'Center', color: 'text-muted-foreground', bg: 'bg-muted' }
  if (bias < 12)
    return {
      label: 'Leans Right',
      color: 'text-red-500 dark:text-red-400',
      bg: 'bg-red-50 dark:bg-red-900/20',
    }
  if (bias < 18)
    return {
      label: 'Right',
      color: 'text-red-600 dark:text-red-400',
      bg: 'bg-red-100 dark:bg-red-900/30',
    }
  return {
    label: 'Far Right',
    color: 'text-red-800 dark:text-red-300',
    bg: 'bg-red-200 dark:bg-red-900/50',
  }
}

// Simplified 3-category version for filtering
export function getBiasCategory(bias) {
  if (bias < -6) return 'left'
  if (bias > 6) return 'right'
  return 'center'
}

export function getReliabilityLabel(reliability) {
  if (reliability >= RELIABILITY.reliable)
    return { label: 'Reliable', color: 'text-green-600 dark:text-green-400' }
  if (reliability >= RELIABILITY.mostly)
    return {
      label: 'Mostly Reliable',
      color: 'text-emerald-600 dark:text-emerald-400',
    }
  if (reliability >= RELIABILITY.mixed)
    return {
      label: 'Mixed Reliability',
      color: 'text-yellow-600 dark:text-yellow-400',
    }
  return { label: 'Low Reliability', color: 'text-red-600 dark:text-red-400' }
}

export function getReliabilityInfo(reliability) {
  if (reliability >= RELIABILITY.reliable)
    return {
      label: 'Reliable',
      description: 'Rated reliable by the watchdogs we use.',
    }
  if (reliability >= RELIABILITY.mostly)
    return {
      label: 'Mostly Reliable',
      description: 'Mostly reliable, with the occasional error.',
    }
  if (reliability >= RELIABILITY.mixed)
    return {
      label: 'Mixed Reliability',
      description: 'Mixed reliability: read with some care.',
    }
  return {
    label: 'Low Reliability',
    description: 'Low reliability: check facts elsewhere.',
  }
}
